import { motion, useAnimation } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';

// Using the same vibrant colors as RetroLoadingDots
const colors = [
  '#FF00FF', // Hot pink
  '#FF6B00', // Orange
  '#00FF9F', // Neon green
  '#FF1493', // Deep pink
  '#FFB800', // Amber
  '#00FFE0', // Cyan
  '#FF4D00', // Bright orange
  '#FF8A00'  // Golden orange
];

const RetroFallingLetters = ({ text = "Loading...", isOpen = true }) => {
  const containerRef = useRef(null);
  const [letters, setLetters] = useState([]);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [isRepelling, setIsRepelling] = useState(false);

  // Initialize letters with physics properties
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setContainerSize({ width, height });

      const initialLetters = text.split('').map((char, index) => ({
        id: `${char}-${index}`,
        char,
        x: Math.random() * (width - 40),
        y: Math.random() * (height - 40),
        velocityX: (Math.random() - 0.5) * 2,
        velocityY: (Math.random() - 0.5) * 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        scale: 1 + Math.random() * 0.5,
        angularVelocity: (Math.random() - 0.5) * 5,
        phase: Math.random() * Math.PI * 2, // Random starting phase for floating
      }));
      setLetters(initialLetters);
    }
  }, [isOpen, text]);

  // Physics animation loop
  useEffect(() => {
    if (!containerRef.current) return;

    let animationFrameId;
    const friction = 0.98;
    const rotationalFriction = 0.95;
    const floatAmplitude = 1;
    const floatFrequency = 0.02;
    const repulsionStrength = isRepelling ? 150 : 0; // Strength of repulsion between letters
    const maxRepulsionDistance = 100; // Maximum distance for repulsion effect

    const updatePhysics = (time) => {
      setLetters(prevLetters => 
        prevLetters.map((letter, i) => {
          let { x, y, velocityX, velocityY, rotation, angularVelocity, phase } = letter;
          
          // Floating motion
          const floatOffset = Math.sin(time * floatFrequency + phase) * floatAmplitude;
          velocityY += floatOffset * 0.1;

          // Repulsion forces between letters
          let totalForceX = 0;
          let totalForceY = 0;

          prevLetters.forEach((otherLetter, j) => {
            if (i !== j) {
              const dx = x - otherLetter.x;
              const dy = y - otherLetter.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              
              if (distance < maxRepulsionDistance && distance > 0) {
                const force = (repulsionStrength / (distance * distance));
                totalForceX += (dx / distance) * force;
                totalForceY += (dy / distance) * force;
              }
            }
          });

          // Apply forces
          velocityX += totalForceX;
          velocityY += totalForceY;
          
          // Update position
          x += velocityX;
          y += velocityY;
          
          // Update rotation
          rotation += angularVelocity;
          angularVelocity *= rotationalFriction;
          
          // Bounce off walls with damping
          if (x < 0 || x > containerSize.width - 40) {
            x = x < 0 ? 0 : containerSize.width - 40;
            velocityX *= -0.5;
          }
          
          if (y < 0 || y > containerSize.height - 40) {
            y = y < 0 ? 0 : containerSize.height - 40;
            velocityY *= -0.5;
          }
          
          // Apply friction
          velocityX *= friction;
          velocityY *= friction;

          return {
            ...letter,
            x,
            y,
            velocityX,
            velocityY,
            rotation,
            angularVelocity,
            phase
          };
        })
      );

      animationFrameId = requestAnimationFrame(updatePhysics);
    };

    animationFrameId = requestAnimationFrame(updatePhysics);
    return () => cancelAnimationFrame(animationFrameId);
  }, [containerSize, isRepelling]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setContainerSize({ width, height });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleContainerClick = (e) => {
    if (!containerRef.current) return;
    
    // Toggle repulsion state
    setIsRepelling(true);
    
    // Add initial explosion force
    const rect = containerRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    setLetters(prevLetters =>
      prevLetters.map(letter => {
        const dx = letter.x - clickX;
        const dy = letter.y - clickY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const force = Math.max(0, 1 - distance / 200) * 30;
        const angle = Math.atan2(dy, dx);
        
        return {
          ...letter,
          velocityX: letter.velocityX + Math.cos(angle) * force,
          velocityY: letter.velocityY + Math.sin(angle) * force,
          angularVelocity: letter.angularVelocity + (Math.random() - 0.5) * 10
        };
      })
    );

    // Reset repulsion after a delay
    setTimeout(() => setIsRepelling(false), 1000);
  };

  return (
    <motion.div 
      ref={containerRef}
      className="absolute inset-0 overflow-hidden rounded-xl cursor-pointer"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={handleContainerClick}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {letters.map((letter) => (
        <motion.div
          key={letter.id}
          className="absolute select-none pointer-events-none"
          animate={{
            x: letter.x,
            y: letter.y,
            rotate: letter.rotation,
            scale: letter.scale
          }}
          style={{
            color: letter.color,
            fontSize: '2.5rem',
            fontFamily: 'VT323',
            textShadow: '0 0 10px rgba(0,0,0,0.5)'
          }}
        >
          {letter.char}
        </motion.div>
      ))}
    </motion.div>
  );
};

export default RetroFallingLetters; 